import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { ArrowLeft, Lock, Loader2, AlertCircle, CheckCircle2, ExternalLink, Building2, Archive, Check, ChevronsUpDown, BookOpen, Upload, PlayCircle } from 'lucide-react';
import { HousePicker } from '../components/legacy-snapshot/HousePicker';
import { PsbDecodeBox, SiteProfilePanel } from '../components/legacy-snapshot/SiteProfilePanel';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { historicalConversionService } from '../services';
import { closingSnapshotPath } from '@/lib/importResume';

// Wave 5A P5.4 — "Add a completed historical stocktake". This page is a thin, plain-English
// front door over the proven Stage 0-8 services (via the P5.3 orchestration API) — it never
// re-implements matching, costing, or any owner-decision gate. Once Stage 1 has run, the
// actual decisions (match review, held rows, acceptance, dispatch) still happen in the
// existing Admin diagnostic PSB screens, linked from here — kept deliberately separate.
const STAGE_ORDER = [
  { key: 'stage1', fallbackLabel: 'Legacy inventory match & price mapping' },
  { key: 'stage2', fallbackLabel: 'Opening stock import' },
  { key: 'stage3', fallbackLabel: 'Deliveries & credits import' },
  { key: 'stage4', fallbackLabel: 'Sales import' },
  { key: 'stage5', fallbackLabel: 'Takings import' },
  { key: 'stage6a', fallbackLabel: 'Closing stock import' },
  { key: 'stage6b', fallbackLabel: 'Bar takings import' },
  { key: 'stage6c', fallbackLabel: 'Allowances & management import' },
  { key: 'stage7', fallbackLabel: 'AbbeyZone cost calculation & reconciliation' },
  { key: 'stage8', fallbackLabel: 'Final dispatch & lock' },
];

// Route A plain-English label overrides for the in-progress wizard stage list.
// Shown instead of backend labels ("Legacy inventory match & price mapping",
// "Opening stock plan (Route A)") which contain internal jargon.
// Reversible: clear map entries or remove the override to restore backend labels.
const ROUTE_A_STAGE_LABEL_MAP = {
  stage1: 'Product matching',
  stage2: 'Opening stock review',
};

function StageStatusBadge({ info }) {
  if (!info) {
    return <Badge variant="outline" className="text-slate-400" data-testid="stage-badge-not-started">Not started</Badge>;
  }
  const status = (info.status || '').toLowerCase();
  if (['applied', 'accepted', 'dispatched', 'scanned'].includes(status)) {
    return <Badge className="bg-emerald-600" data-testid="stage-badge-ok">{status}</Badge>;
  }
  if (status === 'failed' || info.blocked_reason || info.failure_reason) {
    return <Badge variant="destructive" data-testid="stage-badge-blocked">{status || 'blocked'}</Badge>;
  }
  return <Badge variant="secondary" data-testid="stage-badge-pending">{status || 'in progress'}</Badge>;
}

export default function AddHistoricalStocktakePage({ sourceFirst = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeConversionId = searchParams.get('conversion_id');
  // Route A shortcut: ?mode=route-a&site_id=NNN navigates here from StartNewStocktakePage
  // when no prior AZ session exists. Auto-loads archives for the prefilled site so the
  // upload section is immediately visible. The source-first entry (/stocktaking/import/closing-stock)
  // is always a closing-stock (Route A) import and opens on the file upload.
  const routeAMode = sourceFirst || searchParams.get('mode') === 'route-a';
  const prefilledSiteId = searchParams.get('site_id') || '';

  // from-file | pick-site | pick-archive | confirm | starting | progress
  const [step, setStep] = useState(sourceFirst && !resumeConversionId ? 'from-file' : 'pick-site');
  const [sites, setSites] = useState([]);
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archivesLoading, setArchivesLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [archivesError, setArchivesError] = useState(null);

  const [siteId, setSiteId] = useState('');
  const [archiveId, setArchiveId] = useState('');

  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);

  // Pre-flight validation state — populated when transitioning to the confirm step
  const [preflight, setPreflight] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);

  // Explicit destination confirmation — user must tick before starting
  const [confirmedDestination, setConfirmedDestination] = useState(false);

  // Site combobox search state
  const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');

  const [conversion, setConversion] = useState(null);
  const [statusError, setStatusError] = useState(null);

  // effectiveRouteA: true when the URL says route-a OR when the DB-persisted conversion
  // has import_route === 'route_a'. Covers reload / resume where the URL param is absent.
  // Must live AFTER the conversion state declaration to avoid a TDZ crash.
  // Use this for all UI labels and stage filtering; keep routeAMode for URL-triggered effects.
  const effectiveRouteA = routeAMode || (conversion?.import_route === 'route_a');

  // PSB file upload state (for sites with no pre-decoded archives)
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadHouses, setUploadHouses] = useState(null);
  const [fromFileRun, setFromFileRun] = useState(null);

  // Track whether the Route A auto-load has already fired
  const [routeAAutoTriggered, setRouteAAutoTriggered] = useState(false);

  // Historical Stocktake Conversion wave — resume from ?conversion_id query param
  useEffect(() => {
    if (!resumeConversionId) return;
    setLoading(true);
    historicalConversionService.getStatus(resumeConversionId)
      .then(data => {
        setConversion(data);
        setStep('progress');
        if (data.target_site_id) setSiteId(String(data.target_site_id));
      })
      .catch(() => {
        // If status load fails, fall through to the normal pick-site flow
        toast.error('Could not load the saved import. Starting fresh.');
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeConversionId]);

  // Closing-stock imports (Route A) continue in the 3-step snapshot flow once matching is done;
  // an import that already seeded a stocktake stays here as read-only history.
  useEffect(() => {
    if (!effectiveRouteA || !conversion?.conversion_id) return;
    const s1 = conversion.stages?.stage1;
    if (!s1?.readiness || (s1.status || '').toLowerCase().includes('scanning')) return;
    if (s1.readiness.ready) navigate(closingSnapshotPath(conversion.conversion_id), { replace: true });
  }, [effectiveRouteA, conversion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load sites only on mount — archives are fetched per site after selection
  useEffect(() => {
    if (resumeConversionId) return; // skip if we're resuming
    let active = true;
    (async () => {
      try {
        const sitesData = await historicalConversionService.listSites();
        if (!active) return;
        setSites(sitesData?.sites || []);
      } catch (e) {
        if (active) setLoadError('Could not load sites right now. Please try again.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [resumeConversionId]);

  // Auto-load archives when Route A mode provides a prefilled site ID.
  // Fires once, after sites have been fetched, so we can navigate straight to
  // pick-archive (where the upload section lives).
  useEffect(() => {
    if (!routeAMode || !prefilledSiteId || loading || routeAAutoTriggered || resumeConversionId) return;
    if (step !== 'pick-site') return;
    setRouteAAutoTriggered(true);
    setSiteId(prefilledSiteId);
    setArchivesError(null);
    setArchivesLoading(true);
    setArchiveId('');
    setArchives([]);
    historicalConversionService.listArchives(Number(prefilledSiteId))
      .then(data => {
        setArchives(data?.sources || []);
        setStep('pick-archive');
      })
      .catch(() => {
        setArchivesError('Could not load archives for this site. You can still upload a PSB file below.');
        setStep('pick-archive');
      })
      .finally(() => setArchivesLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeAMode, prefilledSiteId, loading, routeAAutoTriggered, resumeConversionId, step]);

  // Poll conversion status when on progress step
  useEffect(() => {
    if (step !== 'progress' || !conversion?.conversion_id) return undefined;
    let active = true;
    const poll = async () => {
      try {
        const data = await historicalConversionService.getStatus(conversion.conversion_id);
        if (active) { setConversion(data); setStatusError(null); }
      } catch (e) {
        if (active) setStatusError('Could not refresh status just now — will retry.');
      }
    };
    poll();
    const interval = setInterval(poll, 4000);
    return () => { active = false; clearInterval(interval); };
  }, [step, conversion?.conversion_id]);

  // Advance from site selector: fetch archives that are compatible with the chosen site
  const handleSiteContinue = async () => {
    if (!siteId) return;
    setArchivesError(null);
    setArchivesLoading(true);
    setArchiveId('');
    setArchives([]);
    try {
      const data = await historicalConversionService.listArchives(Number(siteId));
      setArchives(data?.sources || []);
      setStep('pick-archive');
    } catch (e) {
      setArchivesError('Could not load compatible archives for this site. Please try again.');
    } finally {
      setArchivesLoading(false);
    }
  };

  const handleStart = async () => {
    setStarting(true);
    setStartError(null);
    try {
      const data = await historicalConversionService.start(
        Number(siteId),
        archiveId,
        routeAMode ? 'route_a' : 'route_b',
      );
      setConversion(data);
      setStep('progress');
      if (data.replay) {
        toast.info('A conversion for this site and archive was already started — showing its current status.');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not start this conversion. Please try again.';
      setStartError(msg);
      setStep('confirm'); // stay on confirm so the error is visible
    } finally {
      setStarting(false);
    }
  };

  // PSB file upload handler — decodes archive, then refreshes archive list
  const handleUploadSource = async (house) => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadError(null);
    setUploadResult(null);
    try {
      const result = await historicalConversionService.uploadSource(uploadFile, undefined, typeof house === 'string' ? house : undefined);
      setUploadHouses(null);
      setUploadResult(result);
      toast.success(`PSB archive decoded (house: ${result.house || '?'}). Archive is now available for selection.`);
      // Refresh archive list
      const data = await historicalConversionService.listArchives(Number(siteId));
      setArchives(data?.sources || []);
      // Identity-based auto-select: the decoded archive's run_id is its stage0_run_id
      if (result.run_id) {
        setArchiveId(result.run_id);
      }
      setUploadFile(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === 'multiple_houses') setUploadHouses(d.houses);
      else setUploadError(typeof d === 'string' ? d : d?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };


  const selectedSite = sites.find((s) => String(s.id) === String(siteId));
  const selectedArchive = archives.find((a) => a.stage0_run_id === archiveId);

  // Sites filtered by the combobox search term (case-insensitive name match)
  const filteredSites = siteSearch
    ? sites.filter((s) => s.name.toLowerCase().includes(siteSearch.toLowerCase()))
    : sites;

  return (
    <div className="space-y-6" data-testid="add-historical-stocktake-page">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/stocktaking/import')}
          data-testid="add-historical-back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {effectiveRouteA ? 'Import closing stock from a PSB file' : 'Add a completed historical stocktake'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {effectiveRouteA
              ? 'The closing stock in the file becomes the opening stock for your next stocktake at the site'
              : 'Import a completed legacy stocktake as a locked, read-only AbbeyZone session'}
          </p>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-muted-foreground" data-testid="add-historical-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading sites…
        </div>
      )}

      {!loading && loadError && (
        <Alert variant="destructive" data-testid="add-historical-load-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      )}

      {/* ── STEP 1: pick site ─────────────────────────────────────────────── */}
      {!loading && !loadError && step === 'pick-site' && (
        <Card className="max-w-lg" data-testid="add-historical-pick-site-card">
          <CardHeader>
            <CardTitle className="text-lg">Choose a site</CardTitle>
            <CardDescription>
              All active sites in your organisation are available for historical conversion.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {sites.length === 0 ? (
              <Alert data-testid="add-historical-no-sites">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>No sites are approved for historical conversion yet.</AlertDescription>
              </Alert>
            ) : (
              <Popover
                open={siteDropdownOpen}
                onOpenChange={(open) => {
                  setSiteDropdownOpen(open);
                  if (!open) setSiteSearch('');
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={siteDropdownOpen}
                    className="w-full justify-between font-normal"
                    data-testid="add-historical-site-select"
                  >
                    {selectedSite ? selectedSite.name : 'Select a site'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="p-0"
                  align="start"
                  style={{ width: 'var(--radix-popover-trigger-width)' }}
                >
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search sites…"
                      value={siteSearch}
                      onValueChange={setSiteSearch}
                      data-testid="add-historical-site-search-input"
                    />
                    <CommandList>
                      <CommandEmpty data-testid="add-historical-site-no-results">
                        No sites found.
                      </CommandEmpty>
                      <CommandGroup>
                        {filteredSites.map((s) => (
                          <CommandItem
                            key={s.id}
                            value={String(s.id)}
                            onSelect={() => {
                              setSiteId(String(s.id));
                              setSiteDropdownOpen(false);
                              setSiteSearch('');
                            }}
                            data-testid={`add-historical-site-option-${s.id}`}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                siteId === String(s.id) ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            {s.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
            {archivesError && (
              <Alert variant="destructive" data-testid="add-historical-archives-load-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{archivesError}</AlertDescription>
              </Alert>
            )}
            <Button
              className="w-full"
              disabled={!siteId || archivesLoading}
              onClick={handleSiteContinue}
              data-testid="add-historical-site-continue-button"
            >
              {archivesLoading
                ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading archives…</>)
                : 'Continue'}
            </Button>
            <Button variant="link" className="w-full" onClick={() => { setFromFileRun(null); setStep('from-file'); }} data-testid="add-historical-from-file-button">
              No site yet? Start from the PSB file
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── STEP 1b: first import — read the file, then link or create the site ── */}
      {step === 'from-file' && (
        <Card className="max-w-3xl" data-testid="add-historical-from-file-card">
          <CardHeader>
            <CardTitle className="text-lg">{sourceFirst ? 'Upload your PSB file' : 'Start from the PSB file'}</CardTitle>
            <CardDescription>We read the house, locations and closing stock from the file. Then you choose the matching site, or create it if it isn't in AbbeyZone yet.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!fromFileRun
              ? <PsbDecodeBox onDecoded={(r) => setFromFileRun(r.run_id)} />
              : <SiteProfilePanel stage0RunId={fromFileRun} onSiteReady={async (sid) => {
                  try {
                    const sitesData = await historicalConversionService.listSites();
                    setSites(sitesData?.sites || []);
                    setSiteId(String(sid));
                    const data = await historicalConversionService.listArchives(Number(sid));
                    setArchives(data?.sources || []);
                    setArchiveId(fromFileRun);
                    setStep('pick-archive');
                  } catch (e) {
                    toast.error('The server was slow to respond. The site link is saved — open this file again and click "Continue with this site".');
                  }
                }} />}
            <Button variant="outline" onClick={() => setStep('pick-site')} data-testid="add-historical-from-file-back-button">{sourceFirst ? 'Choose a site first instead' : 'Back'}</Button>
          </CardContent>
        </Card>
      )}

      {/* ── STEP 2: pick archive ──────────────────────────────────────────── */}
      {step === 'pick-archive' && (
        <Card className="max-w-lg" data-testid="add-historical-pick-archive-card">
          <CardHeader>
            <CardTitle className="text-lg">
              {effectiveRouteA ? 'Upload PSB file for closing stock' : 'Choose the legacy archive'}
            </CardTitle>
            <CardDescription>
              {effectiveRouteA
                ? <>Upload your .PSB file for <strong>{selectedSite?.name || 'this site'}</strong>. Only the closing stock position will be extracted as opening stock — no historical transactions will be imported.</>
                : <>Select a decoded PSB archive for <strong>{selectedSite?.name || 'this site'}</strong>. Upload your .PSB file below if no archives are listed.</>}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {archives.length === 0 ? (
              <Alert data-testid="add-historical-no-archives">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No decoded archives are available yet. Upload the .PSB file to decode it first.
                </AlertDescription>
              </Alert>
            ) : (
              <Select value={archiveId} onValueChange={setArchiveId}>
                <SelectTrigger data-testid="add-historical-archive-select">
                  <SelectValue placeholder="Select an archive" />
                </SelectTrigger>
                <SelectContent>
                  {archives.map((a) => (
                    <SelectItem key={a.stage0_run_id} value={a.stage0_run_id}>
                      {a.internal_name || a.house || 'Unnamed'} — {a.period_open || '?'} to {a.period_close || '?'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {selectedArchive && (
              <Alert data-testid="add-historical-archive-preview">
                <AlertDescription>
                  <strong>{selectedArchive.internal_name || selectedArchive.house}</strong> · active products{' '}
                  {selectedArchive.prodfile_active ?? '—'} · active transactions {selectedArchive.tranfile_active ?? '—'}
                </AlertDescription>
              </Alert>
            )}

            {/* PSB file upload — decode a new archive on-demand (admin only) */}
            <div className="rounded-md border border-dashed border-slate-300 p-4 space-y-3" data-testid="add-historical-upload-section">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Upload className="h-4 w-4" />
                Upload .PSB file to decode a new archive
              </div>
              <input
                type="file"
                accept=".psb,.PSB"
                data-testid="add-historical-psb-file-input"
                className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
                onChange={(e) => {
                  setUploadFile(e.target.files?.[0] || null);
                  setUploadHouses(null);
                  setUploadError(null);
                  setUploadResult(null);
                }}
              />
              {uploadError && (
                <Alert variant="destructive" data-testid="add-historical-upload-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{uploadError}</AlertDescription>
                </Alert>
              )}
              {uploadHouses && <HousePicker houses={uploadHouses} onPick={handleUploadSource} busy={uploading} />}
              {uploadResult && (
                <Alert data-testid="add-historical-upload-success">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <AlertDescription>
                    Decoded: <strong>{uploadResult.internal_name || uploadResult.house}</strong> · house {uploadResult.house} · run {uploadResult.run_id?.slice(0, 8)}
                  </AlertDescription>
                </Alert>
              )}
              {!uploadHouses && <Button
                variant="outline"
                size="sm"
                disabled={!uploadFile || uploading}
                onClick={handleUploadSource}
                data-testid="add-historical-upload-button"
              >
                {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Decoding…</> : 'Decode & add archive'}
              </Button>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep('pick-site')} data-testid="add-historical-archive-back-button">
                Back
              </Button>
              <Button
                className="flex-1"
                disabled={!archiveId}
                onClick={async () => {
                  setPreflight(null);
                  setPreflightLoading(true);
                  setConfirmedDestination(false);
                  setStep('confirm');
                  try {
                    const pf = await historicalConversionService.preflight(Number(siteId), archiveId);
                    setPreflight(pf);
                  } catch (e) {
                    // Non-blocking: the confirm step will still show; server re-validates on start
                    setPreflight({ error: e?.response?.data?.detail || 'Preflight check failed — review carefully before starting.' });
                  } finally {
                    setPreflightLoading(false);
                  }
                }}
                data-testid="add-historical-archive-continue-button"
              >
                Review & confirm
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── STEP 3: pre-start confirmation ───────────────────────────────── */}
      {step === 'confirm' && selectedSite && selectedArchive && (
        <Card className="max-w-lg" data-testid="add-historical-confirm-card">
          <CardHeader>
            <CardTitle className="text-lg">Confirm import details</CardTitle>
            <CardDescription>
              Review the destination site and source archive before starting the import.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Preflight loading / results */}
            {preflightLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="add-historical-preflight-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Validating import…
              </div>
            )}
            {preflight?.error && (
              <Alert variant="destructive" data-testid="add-historical-preflight-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{preflight.error}</AlertDescription>
              </Alert>
            )}
            {preflight?.blocking_error && (
              <Alert variant="destructive" data-testid="add-historical-preflight-blocking-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Import not permitted:</strong> {preflight.blocking_error}
                </AlertDescription>
              </Alert>
            )}
            {preflight && !preflight.blocking_error && preflight.duplicate && preflight.completed_session && (
              <Alert className="border-emerald-300 bg-emerald-50" data-testid="add-historical-preflight-completed">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <AlertDescription>
                  <strong>This archive has already been imported.</strong> Session{' '}
                  <strong>{preflight.completed_session.session_reference}</strong> (
                  {preflight.completed_session.session_type === 'stock_audit' ? 'Stock Audit' : 'Valuation'},{' '}
                  {preflight.completed_session.status}) was created from it. Opening it is the safe next
                  step — Continue below only re-opens the same import, it will not create anything new.
                  <div className="mt-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid="add-historical-open-completed-session-btn"
                      onClick={() => navigate(`/stocktaking/sessions/${preflight.completed_session.session_id}`)}
                    >
                      Open Session {preflight.completed_session.session_reference}
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}
            {preflight && !preflight.blocking_error && preflight.duplicate && !preflight.completed_session && (
              <Alert data-testid="add-historical-preflight-duplicate">
                <CheckCircle2 className="h-4 w-4 text-amber-500" />
                <AlertDescription>
                  <strong>Resume existing import</strong> — a conversion for this site and archive is already in progress (status: <em>{preflight.existing_status}</em>). Starting will continue where it left off.
                </AlertDescription>
              </Alert>
            )}
            {preflight && !preflight.blocking_error && !preflight.duplicate && (
              <div className="flex items-center gap-2 text-sm text-emerald-700 font-medium" data-testid="add-historical-preflight-fresh">
                <CheckCircle2 className="h-4 w-4" /> Fresh import — no previous conversion found for this site and archive
              </div>
            )}

            {/* Destination site */}
            <div className="rounded-lg border p-4 space-y-1" data-testid="add-historical-confirm-site">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                <Building2 className="h-3.5 w-3.5" /> Destination site
              </div>
              {/* Show server-confirmed site name when available, with fallback */}
              <div className="font-semibold text-slate-900" data-testid="add-historical-confirm-site-name">
                {preflight?.site_name || selectedSite.name}
              </div>
              <div className="text-sm text-muted-foreground">Site ID {selectedSite.id}</div>
              {/* Warn if the server-side name differs from the locally selected name */}
              {preflight?.site_name && preflight.site_name !== selectedSite.name && (
                <div className="text-xs text-amber-700 font-medium mt-1" data-testid="add-historical-site-name-mismatch">
                  Note: server name "{preflight.site_name}" differs from local selection "{selectedSite.name}" — proceed only if these refer to the same site.
                </div>
              )}
            </div>

            {/* Source archive */}
            <div className="rounded-lg border p-4 space-y-1" data-testid="add-historical-confirm-archive">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                <Archive className="h-3.5 w-3.5" /> Source archive
                {preflight?.import_type && (
                  <span className={`ml-2 px-2 py-0.5 rounded text-[10px] font-semibold ${preflight.import_type === 'resume' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}
                    data-testid="add-historical-import-type-badge">
                    {preflight.import_type === 'resume' ? 'Resume' : 'Fresh import'}
                  </span>
                )}
              </div>
              <div className="font-semibold text-slate-900">
                {selectedArchive.internal_name || selectedArchive.house || 'Unknown'}
              </div>
              <div className="text-sm text-muted-foreground">
                Period: {selectedArchive.period_open || '?'} to {selectedArchive.period_close || '?'}
              </div>
              <div className="text-sm text-muted-foreground">
                Products: {selectedArchive.prodfile_active ?? '—'}{' '}
                {effectiveRouteA
                  ? <span className="text-amber-700">· Transactions: not imported (closing stock only)</span>
                  : <span>· Transactions: {selectedArchive.tranfile_active ?? '—'}</span>}
              </div>
              <div className="text-xs text-muted-foreground font-mono mt-1">
                House: {selectedArchive.house}
              </div>
            </div>

            {startError && (
              <Alert variant="destructive" data-testid="add-historical-start-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{startError}</AlertDescription>
              </Alert>
            )}

            {/* Explicit destination confirmation — required before starting */}
            {!preflight?.blocking_error && (
              <label className="flex items-start gap-3 rounded-lg border border-slate-300 bg-slate-50 p-3 cursor-pointer" data-testid="add-historical-confirm-destination-label">
                <input
                  type="checkbox"
                  id="confirm-destination"
                  checked={confirmedDestination}
                  onChange={e => setConfirmedDestination(e.target.checked)}
                  data-testid="add-historical-confirm-destination-checkbox"
                  className="mt-0.5 h-4 w-4 accent-slate-700"
                />
                <span className="text-sm text-slate-700">
                  I confirm this archive is being imported into{' '}
                  <strong>{preflight?.site_name || selectedSite.name}</strong>. The legacy house number is a reference only; the configured site-to-house relationship governs the destination.
                </span>
              </label>
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setStartError(null); setStep('pick-archive'); }} data-testid="add-historical-confirm-back-button">
                Back
              </Button>
              <Button
                className="flex-1"
                disabled={starting || !!preflight?.blocking_error || !confirmedDestination}
                onClick={handleStart}
                data-testid="add-historical-start-button"
                title={preflight?.blocking_error || (!confirmedDestination ? 'Please confirm the destination site before continuing' : undefined)}
              >
                {starting
                  ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting…</>)
                  : preflight?.import_type === 'resume' ? 'Continue existing import' : 'Confirm & start import'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── STEP 4: progress ─────────────────────────────────────────────── */}
      {step === 'progress' && conversion && (
        <Card className="max-w-2xl" data-testid="add-historical-progress-card">
          <CardHeader>
            <div className="bg-violet-600 w-12 h-12 rounded-lg flex items-center justify-center mb-2">
              <Lock className="h-6 w-6 text-white" />
            </div>
            <CardTitle className="text-lg">
              {effectiveRouteA ? 'Closing stock import in progress' : 'Import in progress'}
            </CardTitle>
            <CardDescription>
              {effectiveRouteA
                ? 'Review the proposed opening stock quantities and costs before creating a draft stocktake.'
                : 'The import is running in the background. Progress updates automatically every few seconds.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {statusError && (
              <Alert variant="destructive" data-testid="add-historical-status-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{statusError}</AlertDescription>
              </Alert>
            )}
            {/* iter-recovery-import — a link back to the created session is shown
                as soon as Stage 2 has produced one, regardless of whether Stage 8
                (final dispatch/lock) has been reached. Most imports stop at
                in-progress, not-yet-published — this is still the clearest,
                safest next action for the user at that point. */}
            {conversion.completed_session && (
              <Alert className="border-emerald-300 bg-emerald-50" data-testid="add-historical-session-ready">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <AlertDescription>
                  Session <strong>{conversion.completed_session.session_reference}</strong> (
                  {conversion.completed_session.session_type === 'stock_audit' ? 'Stock Audit' : 'Valuation'},{' '}
                  {conversion.completed_session.status}) has been created from this import.
                  <div className="mt-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid="add-historical-progress-open-session-btn"
                      onClick={() => navigate(`/stocktaking/sessions/${conversion.completed_session.session_id}`)}
                    >
                      Open Session {conversion.completed_session.session_reference}
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-2" data-testid="add-historical-stage-list">
              {(effectiveRouteA
                ? STAGE_ORDER.filter(s => ['stage1', 'stage2'].includes(s.key))
                : STAGE_ORDER
              ).map(({ key, fallbackLabel }) => {
                const info = conversion.stages?.[key];
                return (
                  <div
                    key={key}
                    className="flex items-center justify-between rounded-md border p-3"
                    data-testid={`add-historical-stage-row-${key}`}
                  >
                    <div>
                      <div className="text-sm font-medium text-slate-900">
                        {(effectiveRouteA && ROUTE_A_STAGE_LABEL_MAP[key]) || info?.label || fallbackLabel}
                      </div>
                      {info?.gate_summary && (
                        <div className="text-xs text-muted-foreground mt-0.5">{info.gate_summary}</div>
                      )}
                      {info?.failure_reason && (
                        <div className="text-xs text-red-600 mt-0.5">{info.failure_reason}</div>
                      )}
                    </div>
                    <StageStatusBadge info={info} />
                  </div>
                );
              })}
            </div>

            {conversion.stages?.stage1?.readiness && (
              <Alert data-testid="add-historical-stage1-summary">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <AlertDescription>{conversion.stages.stage1.readiness.message}</AlertDescription>
              </Alert>
            )}

            {/* Historical Stocktake Conversion wave — direct "Review product matches" action */}
            {(() => {
              const s1RunId = conversion?.stages?.stage1?.run_id || conversion?.stage1_run_id;
              const reviewCount = conversion?.stages?.stage1?.readiness?.pending ?? 0;
              return (
                <div className="flex flex-col gap-2 pt-1">
                  {effectiveRouteA && s1RunId && reviewCount > 0 && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
                      <p className="text-xs text-amber-800 font-medium">
                        {reviewCount} product{reviewCount === 1 ? '' : 's'} in this import still need{reviewCount === 1 ? "s" : ""} a decision before the closing stock can be reviewed.
                      </p>
                      <Link
                        to={`/stocktaking/psb-stage1b-mapping?s1_run_id=${s1RunId}${resumeConversionId ? `&conversion_id=${resumeConversionId}` : ''}`}
                        data-testid="add-historical-review-matches-link"
                      >
                        <Button className="w-full gap-2 bg-amber-600 hover:bg-amber-700 text-white">
                          <BookOpen className="h-4 w-4" />
                          Match products ({reviewCount} remaining)
                        </Button>
                      </Link>
                    </div>
                  )}
                  {effectiveRouteA && s1RunId && reviewCount === 0 && (
                    <Link
                      to={`/stocktaking/psb-stage1b-mapping?s1_run_id=${s1RunId}${resumeConversionId ? `&conversion_id=${resumeConversionId}` : ''}`}
                      data-testid="add-historical-review-matches-link"
                    >
                      <Button variant="outline" className="w-full gap-2 text-xs">
                        <BookOpen className="h-4 w-4" />
                        View matched products
                      </Button>
                    </Link>
                  )}
                  {effectiveRouteA && (resumeConversionId || conversion?.conversion_id) && (
                    <div className="rounded-md border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3" data-testid="add-historical-closing-snapshot-entry">
                      <div className="text-sm text-slate-700 flex-1 min-w-[200px]">
                        <strong>Closing stock snapshot</strong> — review this audit's closing stock, Previous YTD and site prices, then publish it to open the next new stocktake.
                      </div>
                      <Button asChild size="sm" variant="outline" data-testid="add-historical-closing-snapshot-link">
                        <Link to={`/stocktaking/import/closing-snapshot?conversion_id=${resumeConversionId || conversion?.conversion_id}`}>Review closing stock</Link>
                      </Button>
                    </div>
                  )}
                  {/* Route A: Review opening stock and create draft */}
                  {(() => {
                    const s2Info = conversion?.stages?.stage2;
                    const s2Status = s2Info?.status?.toLowerCase();
                    const s2PlanId = s2Info?.run_id;
                    if (!s2PlanId) return null;

                    // Completed state — show after apply, persists on page reload
                    const completedSessionId = s2Info?.created_session_id;
                    if (s2Status === 'applied' && completedSessionId) {
                      return (
                        <Alert className="border-emerald-300 bg-emerald-50" data-testid="add-historical-seed-success">
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          <AlertDescription>
                            <strong>Draft stocktake created</strong> — opening stock seeded.{' '}
                            <Link
                              to={`/stocktaking/sessions/${completedSessionId}`}
                              className="underline text-emerald-800 font-medium"
                              data-testid="add-historical-seed-session-link"
                            >
                              Open draft
                            </Link>
                          </AlertDescription>
                        </Alert>
                      );
                    }

                    return null;
                  })()}
                  <Button
                    variant="ghost"
                    className="w-full text-muted-foreground text-sm"
                    onClick={() => navigate('/stocktaking/import')}
                    data-testid="add-historical-save-exit-button"
                  >
                    Save and exit — your progress is saved automatically
                  </Button>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
